/*
 * upload 上传
 * author zhousheng
 */
import * as React from 'react';
import CSSMotion, { CSSMotionList } from 'rc-motion';
import classNames from 'classnames';
import LoadingOutlined from 'acud-icon/lib/icons/OutlinedLoading';
import PaperClipOutlined from 'acud-icon/lib/icons/OutlinedFileDoc';
import OutlinedFileImage from 'acud-icon/lib/icons/OutlinedFileImage';
import OutlinedFileVideo from 'acud-icon/lib/icons/OutlinedFileVideo';
import OutlinedFileMusic from 'acud-icon/lib/icons/OutlinedFileMusic';
import OutlinedFileZip from 'acud-icon/lib/icons/OutlinedFileZip';
import OutlinedPhoto from 'acud-icon/lib/icons/OutlinedPhoto';
import FileTwoTone from 'acud-icon/lib/icons/OutlinedFile';
import { cloneElement, isValidElement } from '../../_util/reactNode';
import { previewImage, isImageUrl, getFiletypeByName } from '../utils';
import collapseMotion from '../../_util/motion';
import { ConfigContext } from '../../config-provider';
import Button from '../../button';
import useForceUpdate from '../../_util/hooks/useForceUpdate';
import ListItem from './ListItem';
const listItemMotion = Object.assign({}, collapseMotion);
delete listItemMotion.onAppearEnd;
delete listItemMotion.onEnterEnd;
delete listItemMotion.onLeaveEnd;
const renderIcon = (fileName) => {
    const type = getFiletypeByName(fileName);
    let Icon = PaperClipOutlined;
    switch (type) {
        case 'image':
            Icon = OutlinedFileImage;
            break;
        case 'video':
            Icon = OutlinedFileVideo;
            break;
        case 'audio':
            Icon = OutlinedFileMusic;
            break;
        case 'zip':
            Icon = OutlinedFileZip;
            break;
        default:
            break;
    }
    return <Icon width={16}/>;
};
const InternalUploadList = ({ listType, previewFile, onPreview, onDownload, onRemove, onUpload, locale, iconRender, isImageUrl: isImgUrl, prefixCls: customizePrefixCls, items = [], showPreviewIcon, showUploadIcon, showRemoveIcon, showDownloadIcon, removeIcon, downloadIcon, uploadIcon, progress, motionDeadline = 2000, appendAction, itemRender, }, ref) => {
    const forceUpdate = useForceUpdate();
    const [motionAppear, setMotionAppear] = React.useState(false);
    const { getPrefixCls, direction } = React.useContext(ConfigContext);
    // ============================= Render =============================
    const prefixCls = getPrefixCls('upload', customizePrefixCls);
    // ============================= Effect =============================
    React.useEffect(() => {
        if (listType !== 'picture' && listType !== 'picture-card') {
            return;
        }
        (items || []).forEach((file) => {
            if (typeof document === 'undefined'
                || typeof window === 'undefined'
                || !window.FileReader
                || !window.File
                || !(file.originFileObj instanceof File
                    || file.originFileObj instanceof Blob)
                || file.thumbUrl !== undefined) {
                return;
            }
            file.thumbUrl = '';
            if (previewFile) {
                previewFile(file.originFileObj).then((previewDataUrl) => {
                    // Need append '' to avoid dead loop
                    file.thumbUrl = previewDataUrl || '';
                    forceUpdate();
                });
            }
        });
    }, [listType, items, previewFile]);
    React.useEffect(() => {
        setMotionAppear(true);
    }, []);
    // ============================= Events =============================
    const onInternalPreview = (file, e) => {
        if (!onPreview) {
            return;
        }
        e === null || e === void 0 ? void 0 : e.preventDefault();
        return onPreview(file);
    };
    const onInternalDownload = (file) => {
        if (typeof onDownload === 'function') {
            onDownload(file);
        }
        else if (file.url) {
            window.open(file.url);
        }
    };
    const onInternalClose = (file) => {
        onRemove === null || onRemove === void 0 ? void 0 : onRemove(file);
    };
    const onInternalUpload = (file) => {
        onUpload === null || onUpload === void 0 ? void 0 : onUpload(file);
    };
    const internalIconRender = (file) => {
        if (iconRender) {
            return iconRender(file, listType);
        }
        const isLoading = file.status === 'uploading';
        const fileIcon = isImgUrl && isImgUrl(file) ? (<OutlinedPhoto width={24}/>) : (<FileTwoTone width={24}/>);
        let icon = renderIcon(file.name || file.fileName);
        if (listType === 'picture') {
            icon = isLoading ? <LoadingOutlined animation="spin"/> : fileIcon;
        }
        else if (listType === 'picture-card') {
            icon = isLoading ? `${locale.uploading}...` : fileIcon;
        }
        return icon;
    };
    const actionIconRender = (customIcon, callback, prefixCls, title) => {
        const btnProps = {
            type: 'text',
            size: 'small',
            title,
            onClick: (e) => {
                callback();
                if (isValidElement(customIcon) && customIcon.props.onClick) {
                    customIcon.props.onClick(e);
                }
            },
            className: `${prefixCls}-list-item-card-actions-btn`,
        };
        if (isValidElement(customIcon)) {
            const btnIcon = cloneElement(customIcon, Object.assign(Object.assign({}, customIcon.props), { onClick: () => { } }));
            return <Button {...btnProps} icon={btnIcon}/>;
        }
        return (<Button {...btnProps}>
                <span>{customIcon}</span>
            </Button>);
    };
    // ============================== Ref ===============================
    // Test needs
    React.useImperativeHandle(ref, () => ({
        handlePreview: onInternalPreview,
        handleDownload: onInternalDownload,
    }));
    // >>> Motion config
    const motionKeyList = [
        ...items.map(file => ({
            key: file.uid,
            file,
        })),
    ];
    const animationDirection = listType === 'picture-card' ? 'animate-inline' : 'animate';
    // const transitionName = list.length === 0 ? '' : `${prefixCls}-${animationDirection}`;
    let motionConfig = {
        motionDeadline,
        motionName: `${prefixCls}-${animationDirection}`,
        keys: motionKeyList,
        motionAppear,
    };
    if (listType !== 'picture-card') {
        motionConfig = Object.assign(Object.assign({}, listItemMotion), motionConfig);
    }
    const listClassNames = classNames({
        [`${prefixCls}-list`]: true,
        [`${prefixCls}-list-${listType}`]: true,
        [`${prefixCls}-list-rtl`]: direction === 'rtl',
    });
    return (<div className={listClassNames}>
            {/* Append action */}
            {appendAction && (<CSSMotion {...motionConfig}>
                    {({ className: motionClassName, style: motionStyle }) => cloneElement(appendAction, oriProps => ({
                className: classNames(oriProps.className, motionClassName),
                style: Object.assign(Object.assign({}, motionStyle), oriProps.style),
            }))}
                </CSSMotion>)}
            <CSSMotionList {...motionConfig} component={false}>
                {({ key, file, className: motionClassName, style: motionStyle, }) => (<ListItem key={key} locale={locale} prefixCls={prefixCls} className={motionClassName} style={motionStyle} file={file} items={items} progress={progress} listType={listType} isImgUrl={isImgUrl} showPreviewIcon={showPreviewIcon} showUploadIcon={showUploadIcon} showRemoveIcon={showRemoveIcon} showDownloadIcon={showDownloadIcon} removeIcon={removeIcon} downloadIcon={downloadIcon} uploadIcon={uploadIcon} iconRender={internalIconRender} actionIconRender={actionIconRender} itemRender={itemRender} onPreview={onInternalPreview} onDownload={onInternalDownload} onClose={onInternalClose} onUpload={onInternalUpload} motionDeadline={motionDeadline}/>)}
            </CSSMotionList>
        </div>);
};
const UploadList = React.forwardRef(InternalUploadList);
UploadList.displayName = 'UploadList';
UploadList.defaultProps = {
    listType: 'text',
    progress: {
    // strokeWidth: 4,
    // showInfo: false
    },
    showUploadIcon: true,
    motionDeadline: 2000,
    showRemoveIcon: true,
    showDownloadIcon: false,
    showPreviewIcon: true,
    previewFile: previewImage,
    isImageUrl,
};
export default UploadList;
